import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mountain, MapPin, Heart, ArrowLeft, Share2, Trash2 } from 'lucide-react';
import { toFeaturedSlug } from './FeaturedStay';
import TAGLINE_MAP from '../data/taglineData';
import { useWishlist } from '../hooks/useWishlist';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getPartnerSlug = (name) => {
  if (!name) return null;
  const n = name.toLowerCase();
  if (n.includes('bluehouse') || n.includes('blue house')) return 'bluehouse';
  if (n.includes("eagle's nest") || n.includes('eagles nest') || n.includes('eaglenest')) return 'eaglenest';
  if (n.includes('tra boutique') || n.includes('tra,')) return 'tra';
  if (n.includes('himalayan homestead')) return 'homestead';
  if (n.includes('raju')) return 'rajucottage';
  if (n.includes('brown dipper') || n.includes('browndipper')) return 'browndipper';
  if (n.includes('villa aavaas') || n.includes('villaaavaas')) return 'villaaavaas';
  if (n.includes('srirangam')) return 'srirangam';
  return null;
};

export default function Wishlist() {
  const [allStays, setAllStays] = useState([]);
  const [loading, setLoading] = useState(true);
  const { wishlist, isWishlisted, toggleWishlist, getLikeCount } = useWishlist();

  useEffect(() => {
    fetch(`${API_URL}/api/curated/stays?limit=200`)
      .then(r => r.json())
      .then(d => { setAllStays(d.stays || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const wishlisted = allStays.filter(s => wishlist.includes(s.gohimalayan_id));

  const shareWishlist = () => {
    if (wishlisted.length === 0) return;
    const names = wishlisted.map(s => s.display_name?.split(',')[0]).join(', ');
    const msg = `Check out my Himalayan travel shortlist on GoHimalayan:\n\n${wishlisted.map(s => {
      const ps = s.partnership_status === 'partner' ? getPartnerSlug(s.display_name) : null;
      const slug = ps || toFeaturedSlug(s.display_name);
      const path = ps ? `/collective/${ps}` : `/stay/${slug}`;
      return `${s.display_name?.split(',')[0]} — gohimalayan.com${path}`;
    }).join('\n')}\n\nDiscover handpicked stays across the Himalayas at gohimalayan.com`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="wishlist-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/discovered" className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Stays</span>
            </Link>
            <div className="flex items-center gap-2.5">
              <Heart className="h-5 w-5 text-red-500 fill-red-500" />
              <span className="text-lg font-bold tracking-tight text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                My Wishlist
              </span>
            </div>
            {wishlisted.length > 0 && (
              <button
                onClick={shareWishlist}
                className="flex items-center gap-2 px-4 py-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 rounded-full text-[#25D366] text-xs font-medium tracking-wider uppercase transition-colors"
                data-testid="share-wishlist-btn"
              >
                <Share2 className="w-3.5 h-3.5" />
                Share via WhatsApp
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="pt-28 pb-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#2C5F8D]/30 border-t-[#2C5F8D] rounded-full animate-spin" />
            </div>
          ) : wishlisted.length === 0 ? (
            <div className="text-center py-20">
              <Heart className="w-12 h-12 text-gray-200 mx-auto mb-4" />
              <h2 className="text-xl text-gray-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>No saved stays yet</h2>
              <p className="text-gray-400 text-sm mb-6">Tap the heart icon on any property to save it here.</p>
              <Link to="/discovered" className="inline-flex items-center gap-2 px-6 py-3 bg-[#2C5F8D] text-white rounded-full text-sm font-medium hover:bg-[#1e4a6e] transition-colors">
                <Mountain className="w-4 h-4" />
                Discover Stays
              </Link>
            </div>
          ) : (
            <>
              <p className="text-gray-400 text-sm mb-8">{wishlisted.length} saved {wishlisted.length === 1 ? 'stay' : 'stays'}</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {wishlisted.map(stay => {
                  const partnerSlug = stay.partnership_status === 'partner' ? getPartnerSlug(stay.display_name) : null;
                  const featuredSlug = !partnerSlug ? toFeaturedSlug(stay.display_name) : null;
                  const taglineSlug = partnerSlug || featuredSlug;
                  const customTagline = TAGLINE_MAP[taglineSlug] || stay.tagline || '';
                  const linkTo = partnerSlug ? `/collective/${partnerSlug}` : `/stay/${featuredSlug}`;

                  return (
                    <div key={stay.gohimalayan_id} className="bg-white rounded-2xl overflow-hidden border border-gray-100/60 hover:shadow-lg transition-all group">
                      <Link to={linkTo}>
                        <div className="aspect-[4/3] bg-gradient-to-br from-[#2C5F8D]/20 to-[#2C5F8D]/5 overflow-hidden relative">
                          {stay.images?.[0]?.url ? (
                            <img src={stay.images[0].url} alt={stay.display_name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Mountain className="w-12 h-12 text-[#2C5F8D]/30" />
                            </div>
                          )}
                          {/* Like count */}
                          <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full">
                            <Heart className="w-3.5 h-3.5 fill-red-500 text-red-500" />
                            <span className="text-[10px] text-white/80 font-medium">{getLikeCount(stay.gohimalayan_id)}</span>
                          </div>
                        </div>
                      </Link>

                      <div className="p-5">
                        <Link to={linkTo}>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                            {stay.display_name}
                          </h3>
                          {customTagline && (
                            <p className="text-sm text-gray-500 italic mb-2">"{customTagline}"</p>
                          )}
                          <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                            <MapPin className="w-3 h-3" />
                            <span>{stay.region}, {stay.state}</span>
                          </div>
                        </Link>

                        {/* Remove from wishlist */}
                        <button
                          onClick={() => toggleWishlist(stay.gohimalayan_id)}
                          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 transition-colors"
                          data-testid={`remove-wishlist-${stay.gohimalayan_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
